import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { MapContainer, TileLayer, CircleMarker, Tooltip as LTooltip } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { cartoTileUrl } from '../lib/basemap';
import {
  Building2, MapPin, ArrowLeft, MessageSquare, ExternalLink, Calendar, AlertTriangle,
} from 'lucide-react';

type TimelineItem = {
  id: string; title: string; sourceUrl: string; shortSummary: string;
  documentType: string | null; signal: string | null; organization: string | null;
  city: string | null; province: string | null;
  publishedAt: string | null; dateCrawled: string; datedByPublication: boolean; tags: string[];
};
type Profile = {
  entity: { type: 'org' | 'place'; name: string; province: string | null };
  summary: {
    total: number;
    span: { from: string; to: string } | null;
    outputTypes: { documentType: string; count: number }[];
    signals: { signal: string; count: number }[];
    topTags: { name: string; count: number }[];
    organizations?: { name: string; count: number }[];
    places?: string[];
  };
  timeline: TimelineItem[];
  projects: { documentId: string; title: string; url: string | null; fileName: string }[];
  coords: { lat: number; lng: number } | null;
};

const fmt = (d?: string | null) => (d ? new Date(d).toLocaleDateString('en-CA', { year: 'numeric', month: 'short', day: 'numeric' }) : '');
const DOC_LABEL: Record<string, string> = {
  inventory: 'Inventory', valuation: 'Valuation', 'asset-management-plan': 'Asset Mgmt Plan',
  'financial-report': 'Financial Report', policy: 'Policy', strategy: 'Strategy',
  'case-study': 'Case Study', funding: 'Funding', news: 'News', other: 'Other',
};

function Chip({ children, tone = 'default' }: { children: React.ReactNode; tone?: 'default' | 'accent' | 'warn' | 'good' }) {
  const tones: Record<string, string> = {
    default: 'bg-[var(--surface)] border-[var(--border)] text-[color:var(--text-secondary)]',
    accent: 'bg-[var(--surface)] border-[var(--border)] text-[color:var(--accent-blue)]',
    good: 'bg-[var(--surface)] border-[var(--border)] text-[color:var(--accent)]',
    warn: 'bg-[rgba(245,158,11,0.12)] border-[rgba(245,158,11,0.4)] text-[#f59e0b]',
  };
  return <span className={`inline-flex items-center gap-1 text-[11px] px-2 py-[2px] rounded-full border ${tones[tone]}`}>{children}</span>;
}

export default function EntityProfile() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const type = params.get('type') as 'org' | 'place' | null;
  const name = params.get('name') || '';
  const province = params.get('province') || '';

  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [data, setData] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!type || !name) return;
    setLoading(true);
    const qp = new URLSearchParams({ type, name });
    if (province) qp.set('province', province);
    if (start) qp.set('start', start);
    if (end) qp.set('end', end);
    axios.get(`/api/entities/profile?${qp}`)
      .then(r => setData(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [type, name, province, start, end]);

  const askAssistant = () => {
    const where = province ? `${name}, ${province}` : name;
    navigate('/nam-ai', { state: { seedPrompt: `Summarize natural asset management progress for ${where}. What has been done, what outputs (inventories, valuations, plans, reports) exist, and what stage is the work at?` } });
  };

  // Group timeline by year of the coalesced date.
  const byYear = useMemo(() => {
    const groups = new Map<string, TimelineItem[]>();
    for (const it of data?.timeline || []) {
      const y = String(new Date(it.publishedAt || it.dateCrawled).getFullYear());
      if (!groups.has(y)) groups.set(y, []);
      groups.get(y)!.push(it);
    }
    return Array.from(groups.entries());
  }, [data]);

  if (!type || !name) return <div className="p-8 text-[color:var(--text-muted)]">Missing entity. <Link to="/entities" className="text-[color:var(--accent-blue)]">Back to directory</Link></div>;

  const s = data?.summary;

  return (
    <div className="bg-transparent min-h-full p-8">
      <Link to="/entities" className="inline-flex items-center gap-1 text-[12px] text-[color:var(--text-muted)] hover:text-[color:var(--text-primary)] mb-4">
        <ArrowLeft size={14} /> Entities
      </Link>

      {/* Header */}
      <div className="flex items-start gap-4 mb-6 flex-wrap">
        <div className="shrink-0 w-12 h-12 rounded-[12px] bg-[var(--surface)] border border-[var(--border)] flex items-center justify-center text-[color:var(--accent)]">
          {type === 'org' ? <Building2 size={24} /> : <MapPin size={24} />}
        </div>
        <div className="min-w-0 flex-1">
          <h1 className="text-[22px] font-semibold text-[color:var(--text-primary)] m-0">{name}</h1>
          <p className="text-[13px] text-[color:var(--text-muted)] m-0">
            {type === 'org' ? 'Organization' : 'Place'}{province ? ` · ${province}` : ''}
            {s?.span ? ` · active ${fmt(s.span.from)} – ${fmt(s.span.to)}` : ''}
          </p>
        </div>
        <button onClick={askAssistant} className="inline-flex items-center gap-2 text-[13px] px-3 py-2 rounded-[10px] bg-[var(--accent)] text-white hover:opacity-90 transition-opacity">
          <MessageSquare size={15} /> Ask the assistant
        </button>
      </div>

      {loading && <p className="text-[13px] text-[color:var(--text-muted)]">Loading…</p>}

      {!loading && data && (
        <div className="grid gap-6 [grid-template-columns:minmax(0,1fr)] lg:[grid-template-columns:minmax(0,2fr)_minmax(260px,1fr)]">
          {/* Left: summary + timeline */}
          <div className="min-w-0">
            {/* Summary band */}
            <div className="bg-[var(--glass-bg)] backdrop-blur-[12px] border border-[var(--glass-border)] rounded-[16px] p-5 mb-6">
              <div className="flex flex-wrap gap-x-8 gap-y-4">
                <div>
                  <div className="text-[11px] uppercase tracking-wide text-[color:var(--text-muted)] mb-2">Outputs produced</div>
                  <div className="flex flex-wrap gap-[6px]">
                    {s!.outputTypes.length ? s!.outputTypes.map(o => (
                      <Chip key={o.documentType} tone="accent">{DOC_LABEL[o.documentType] || o.documentType} · {o.count}</Chip>
                    )) : <span className="text-[12px] text-[color:var(--text-muted)]">—</span>}
                  </div>
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-wide text-[color:var(--text-muted)] mb-2">Signal</div>
                  <div className="flex flex-wrap gap-[6px]">
                    {s!.signals.length ? s!.signals.map(sg => (
                      <Chip key={sg.signal} tone={sg.signal === 'maladaptation' ? 'warn' : sg.signal === 'positive' ? 'good' : 'default'}>
                        {sg.signal === 'maladaptation' && <AlertTriangle size={11} />}{sg.signal} · {sg.count}
                      </Chip>
                    )) : <span className="text-[12px] text-[color:var(--text-muted)]">—</span>}
                  </div>
                </div>
              </div>
              {s!.topTags.length > 0 && (
                <div className="mt-4">
                  <div className="text-[11px] uppercase tracking-wide text-[color:var(--text-muted)] mb-2">Top themes</div>
                  <div className="flex flex-wrap gap-[6px]">
                    {s!.topTags.map(t => <Chip key={t.name}>{t.name} · {t.count}</Chip>)}
                  </div>
                </div>
              )}
            </div>

            {/* Date-range filter */}
            <div className="flex items-center gap-3 mb-4 flex-wrap">
              <span className="text-[12px] text-[color:var(--text-muted)] inline-flex items-center gap-1"><Calendar size={13} /> Time period</span>
              <input type="date" value={start} onChange={e => setStart(e.target.value)} className="bg-[var(--surface)] border border-[var(--border)] rounded-[8px] px-2 py-1 text-[12px] text-[color:var(--text-primary)]" />
              <span className="text-[12px] text-[color:var(--text-muted)]">to</span>
              <input type="date" value={end} onChange={e => setEnd(e.target.value)} className="bg-[var(--surface)] border border-[var(--border)] rounded-[8px] px-2 py-1 text-[12px] text-[color:var(--text-primary)]" />
              {(start || end) && <button onClick={() => { setStart(''); setEnd(''); }} className="text-[12px] text-[color:var(--accent-blue)]">clear</button>}
              <span className="text-[12px] text-[color:var(--text-muted)] ml-auto">{data.timeline.length} item{data.timeline.length === 1 ? '' : 's'}</span>
            </div>

            {/* Timeline */}
            {byYear.length === 0 && <p className="text-[13px] text-[color:var(--text-muted)]">No items in this period.</p>}
            {byYear.map(([year, items]) => (
              <div key={year} className="mb-5">
                <div className="text-[13px] font-semibold text-[color:var(--text-primary)] mb-2 sticky top-0">{year}</div>
                <div className="flex flex-col gap-[10px]">
                  {items.map(it => (
                    <div key={it.id} className="bg-[var(--glass-bg)] border border-[var(--glass-border)] rounded-[12px] p-4">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <span className="text-[11px] text-[color:var(--text-muted)] inline-flex items-center gap-1">
                          {fmt(it.publishedAt || it.dateCrawled)}
                          {!it.datedByPublication && <span title="Crawl date — no publication date in source" className="opacity-70">(crawled)</span>}
                        </span>
                        {it.documentType && <Chip tone="accent">{DOC_LABEL[it.documentType] || it.documentType}</Chip>}
                        {it.signal === 'maladaptation' && <Chip tone="warn"><AlertTriangle size={11} />maladaptation</Chip>}
                      </div>
                      <a href={it.sourceUrl} target="_blank" rel="noreferrer" className="text-[14px] font-medium text-[color:var(--text-primary)] hover:text-[color:var(--accent-blue)] inline-flex items-center gap-1">
                        {it.title || it.sourceUrl} <ExternalLink size={12} className="shrink-0 opacity-60" />
                      </a>
                      {it.shortSummary && <p className="text-[13px] text-[color:var(--text-secondary)] mt-1 mb-2">{it.shortSummary}</p>}
                      {it.tags.length > 0 && <div className="flex flex-wrap gap-[5px]">{it.tags.slice(0, 5).map(t => <Chip key={t}>{t}</Chip>)}</div>}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Right: map + related */}
          <div className="min-w-0">
            {data.coords && (
              <div className="bg-[var(--glass-bg)] border border-[var(--glass-border)] rounded-[16px] overflow-hidden mb-6 h-[220px]">
                <MapContainer center={[data.coords.lat, data.coords.lng]} zoom={9} className="w-full h-full" attributionControl={false} scrollWheelZoom={false}>
                  <TileLayer url={cartoTileUrl('rastertiles/voyager')} subdomains="abcd" maxZoom={20} />
                  <CircleMarker center={[data.coords.lat, data.coords.lng]} radius={9} pathOptions={{ color: '#10b981', fillColor: '#10b981', fillOpacity: 0.6 }}>
                    <LTooltip>{name}</LTooltip>
                  </CircleMarker>
                </MapContainer>
              </div>
            )}

            {type === 'place' && data.summary.organizations && data.summary.organizations.length > 0 && (
              <div className="bg-[var(--glass-bg)] border border-[var(--glass-border)] rounded-[16px] p-4 mb-6">
                <div className="text-[11px] uppercase tracking-wide text-[color:var(--text-muted)] mb-2">Organizations active here</div>
                <div className="flex flex-col gap-1">
                  {data.summary.organizations.map(o => (
                    <Link key={o.name} to={`/entities/profile?type=org&name=${encodeURIComponent(o.name)}`} className="text-[13px] text-[color:var(--text-primary)] hover:text-[color:var(--accent-blue)] flex justify-between">
                      <span className="truncate">{o.name}</span><span className="text-[color:var(--text-muted)] ml-2">{o.count}</span>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {type === 'org' && data.summary.places && data.summary.places.length > 0 && (
              <div className="bg-[var(--glass-bg)] border border-[var(--glass-border)] rounded-[16px] p-4 mb-6">
                <div className="text-[11px] uppercase tracking-wide text-[color:var(--text-muted)] mb-2">Places</div>
                <div className="flex flex-wrap gap-[6px]">{data.summary.places.map(p => <Chip key={p}>{p}</Chip>)}</div>
              </div>
            )}

            {data.projects.length > 0 && (
              <div className="bg-[var(--glass-bg)] border border-[var(--glass-border)] rounded-[16px] p-4">
                <div className="text-[11px] uppercase tracking-wide text-[color:var(--text-muted)] mb-2">Related project documents</div>
                <div className="flex flex-col gap-2">
                  {data.projects.map(p => (
                    p.url
                      ? <a key={p.documentId} href={p.url} target="_blank" rel="noreferrer" className="text-[13px] text-[color:var(--text-primary)] hover:text-[color:var(--accent-blue)] inline-flex items-center gap-1">{p.title} <ExternalLink size={11} className="opacity-60" /></a>
                      : <span key={p.documentId} className="text-[13px] text-[color:var(--text-secondary)]">{p.title}</span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
